import { useMemo } from 'react'

import { Dayjs } from '@bemlo/date'
import { arrayOfAll } from '@bemlo/utils'

import { useTranslations } from '../../translate'

export type RangeType = 'week' | 'two-weeks' | 'month' | 'custom'

export const ALL_RANGE_TYPES = arrayOfAll<RangeType>()([
  'custom',
  'month',
  'two-weeks',
  'week',
])

Object.freeze(ALL_RANGE_TYPES)

type RangeTypeOption = {
  value: RangeType
  label: string
  todayLabel: string
  getToday: undefined | (() => [Dayjs, Dayjs])
  getFirstDate: undefined | ((date: Dayjs) => Dayjs)
  getLastDate: undefined | ((date: Dayjs) => Dayjs)
  getMovedDate: undefined | ((date: Dayjs, delta: 1 | -1) => Dayjs)
  getLabel: undefined | ((date: Dayjs) => string)
}

export const useRangeTypes = (
  selected: RangeType | undefined,
  enabled: RangeType[] = ALL_RANGE_TYPES,
) => {
  const t = useTranslations()

  const options: RangeTypeOption[] = [
    {
      value: 'week',
      label: t.form.week(),
      todayLabel: t.common.this_week(),
      getToday: () => {
        const startDate = t.$date().startOf('week')
        const endDate = startDate.endOf('week')
        return [startDate, endDate]
      },
      getFirstDate: (date) => date.startOf('week'),
      getLastDate: (date) => date.endOf('week'),
      getMovedDate: (date, delta) => date.add(delta, 'week'),
      getLabel: (date) => date.formatWeek(),
    },
    {
      value: 'two-weeks',
      label: t.form.n_weeks(2),
      todayLabel: t.common.this_week(),
      getToday: () => {
        const startDate = t.$date().startOf('week')
        const endDate = startDate.add(1, 'week').endOf('week')
        return [startDate, endDate]
      },
      getFirstDate: (date) => date.startOf('week'),
      getLastDate: (date) => date.endOf('week').add(1, 'week'),
      getMovedDate: (date, delta) => date.add(delta * 2, 'week'),
      getLabel: (date) => date.formatWeekRange(date.add(1, 'week'), true),
    },
    {
      value: 'month',
      label: t.form.month(),
      todayLabel: t.common.this_month(),
      getToday: () => {
        const startDate = t.$date().startOf('week')
        const endDate = startDate.endOf('month')
        return [startDate, endDate]
      },
      getFirstDate: (date) => date.startOf('month'),
      getLastDate: (date) => date.endOf('month'),
      getMovedDate: (date, delta) => date.add(delta, 'month'),
      getLabel: (date) => date.format('MMMM YYYY'),
    },
    {
      value: 'custom',
      label: t.form.custom(),
      todayLabel: t.common.today(),
      getToday: undefined,
      getFirstDate: undefined,
      getLastDate: undefined,
      getMovedDate: undefined,
      getLabel: undefined,
    },
  ]

  const selectedOption = options.find((option) => option.value === selected)

  return useMemo(() => {
    return {
      options: options.filter((option) => enabled.includes(option.value)),
      selectedOption,
    }
  }, [selected, t, enabled])
}
